/**
 * Channels, on the in-memory store.
 *
 * Its own file rather than another section of index.js, which is already
 * thirty kilobytes — and this service is the one with a privacy rule to keep,
 * which is easier to see in a file that holds nothing else.
 *
 * The follower list exists here because something has to hold it, and it
 * never leaves this file. No method below returns it: the same guarantee the
 * server makes, kept the same way — by not writing the query rather than by
 * guarding one.
 */

import { state, subscribe, emit, latency } from './store.js'
import { toChannel, toChannelPost, normaliseHandle } from '../../models/index.js'
import { newId } from '../../utils/id.js'
import { ZivError, ErrorCode } from '../errors.js'

const me = () => state.currentUserId

/** Store record → the shape the contract promises, caller's view folded in. */
function channelView (record) {
  const follow = record.followers.get(me())
  const role = record.admins.get(me())
  const live = record.posts.filter(p => !p.deletedAt)
  const last = live[live.length - 1] ?? null
  const since = follow?.lastReadAt ?? follow?.followedAt ?? record.createdAt

  return {
    id:            record.id,
    handle:        record.handle,
    name:          record.name,
    description:   record.description,
    avatarUrl:     record.avatarUrl,
    isVerified:    record.isVerified,
    followerCount: record.followers.size,
    isFollowing:   record.followers.has(me()),
    isMuted:       Boolean(follow?.isMuted),
    isAdmin:       Boolean(role),
    isOwner:       role === 'owner',
    unreadCount:   live.filter(p => p.createdAt > since).length,
    lastPost:      last
      ? { id: last.id, type: last.type, preview: last.body ?? '', createdAt: last.createdAt }
      : null,
    createdBy:     record.createdBy,
    createdAt:     record.createdAt
  }
}

const asChannel = record => toChannel(record.id, channelView(record))

/** Posts live inside their channel, so a post id has to be hunted for. */
function findPost (postId) {
  for (const record of state.channels.values()) {
    const post = record.posts.find(p => p.id === postId)
    if (post) return { record, post }
  }
  return { record: null, post: null }
}

function requireChannel (channelId) {
  const record = state.channels.get(channelId)
  if (!record) throw new ZivError(ErrorCode.NOT_FOUND, 'Channel not found.')
  return record
}

function requireAdmin (record) {
  if (!record.admins.has(me())) {
    throw new ZivError(ErrorCode.PERMISSION, 'Only an admin can do that.')
  }
}

export const channels = {
  subscribeToMine (callback) {
    const send = () => callback(
      [...state.channels.values()]
        .filter(r => r.followers.has(me()) || r.admins.has(me()))
        .map(asChannel)
        .sort((a, b) =>
          (b.lastPost?.createdAt ?? b.createdAt) - (a.lastPost?.createdAt ?? a.createdAt))
    )
    queueMicrotask(send)
    return subscribe('channels', send)
  },

  subscribeToPosts (channelId, callback) {
    const send = () => {
      const record = state.channels.get(channelId)
      if (!record) return callback([])
      callback(record.posts.filter(p => !p.deletedAt).map(p => toChannelPost(p.id, p)))
    }
    queueMicrotask(send)
    // The REST provider filters on channelId because the socket frame names
    // one channel. Here every channel shares a topic, so this just re-reads.
    return subscribe('channels', send)
  },

  async get (channelId) {
    await latency()
    return asChannel(requireChannel(channelId))
  },

  async getByHandle (handle) {
    await latency()
    const wanted = String(handle ?? '').replace(/^@/, '').toLowerCase()
    for (const record of state.channels.values()) {
      if (record.handle === wanted) return asChannel(record)
    }
    return null
  },

  async search (term = '') {
    await latency()
    const needle = term.trim().toLowerCase().replace(/^@/, '')
    const all = [...state.channels.values()]
    const matched = needle
      ? all.filter(r =>
          r.name.toLowerCase().includes(needle) ||
          r.handle.includes(needle) ||
          (r.description ?? '').toLowerCase().includes(needle))
      : all
    return matched.map(asChannel).sort((a, b) => b.followerCount - a.followerCount)
  },

  async create ({ name, handle, description = null, avatarUrl = null }) {
    await latency()
    const parsed = normaliseHandle(handle)
    if (!parsed.ok) throw new ZivError(ErrorCode.INVALID_INPUT, parsed.reason)
    if (!String(name ?? '').trim()) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'A channel needs a name.')
    }
    for (const record of state.channels.values()) {
      if (record.handle === parsed.handle) {
        throw new ZivError(ErrorCode.CONFLICT, 'That handle is already taken.')
      }
    }

    const id = `ch_${newId()}`
    const createdAt = new Date()
    state.channels.set(id, {
      id,
      handle:      parsed.handle,
      name:        String(name).trim(),
      description,
      avatarUrl,
      isVerified:  false,
      createdBy:   me(),
      createdAt,
      admins:      new Map([[me(), 'owner']]),
      // The creator follows their own channel, or its first post never
      // reaches the person who made it, which reads as a broken channel.
      followers:   new Map([[me(), { isMuted: false, lastReadAt: null, followedAt: createdAt }]]),
      posts:       []
    })

    emit('channels')
    return asChannel(state.channels.get(id))
  },

  async update (channelId, patch) {
    await latency()
    const record = requireChannel(channelId)
    requireAdmin(record)
    // No handle here, on purpose: links already point at it.
    if (patch.name !== undefined) record.name = String(patch.name).trim()
    if (patch.description !== undefined) record.description = patch.description ?? null
    if (patch.avatarUrl !== undefined) record.avatarUrl = patch.avatarUrl ?? null
    emit('channels')
    return asChannel(record)
  },

  async follow (channelId) {
    await latency()
    const record = requireChannel(channelId)
    if (!record.followers.has(me())) {
      record.followers.set(me(), { isMuted: false, lastReadAt: null, followedAt: new Date() })
    }
    emit('channels')
    return asChannel(record)
  },

  async unfollow (channelId) {
    await latency()
    requireChannel(channelId).followers.delete(me())
    emit('channels')
  },

  async setMuted (channelId, isMuted) {
    await latency(60)
    const follow = requireChannel(channelId).followers.get(me())
    if (!follow) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'Follow the channel before muting it.')
    }
    follow.isMuted = Boolean(isMuted)
    emit('channels')
  },

  async post (channelId, { type = 'text', body = null, mediaUrl = null, mediaMeta = null } = {}) {
    await latency()
    const record = requireChannel(channelId)
    requireAdmin(record)
    if (!String(body ?? '').trim() && !mediaUrl) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'A post needs either text or media.')
    }

    const post = {
      id:        `cp_${newId()}`,
      channelId,
      authorId:  me(),
      type,
      body,
      mediaUrl,
      mediaMeta,
      viewCount: 0,
      reactions: {},
      createdAt: new Date(),
      editedAt:  null,
      deletedAt: null
    }
    record.posts.push(post)
    emit('channels')
    return toChannelPost(post.id, post)
  },

  async editPost (postId, body) {
    await latency()
    const { record, post } = findPost(postId)
    if (!post || post.deletedAt) throw new ZivError(ErrorCode.NOT_FOUND, 'Post not found.')
    requireAdmin(record)
    if (!String(body ?? '').trim()) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'A post needs text.')
    }
    post.body = body
    post.editedAt = new Date()
    emit('channels')
    return toChannelPost(post.id, post)
  },

  async deletePost (postId) {
    await latency()
    const { record, post } = findPost(postId)
    if (!post || post.deletedAt) throw new ZivError(ErrorCode.NOT_FOUND, 'Post not found.')
    requireAdmin(record)
    // Soft, like the server: the tallies and the view count survive.
    post.deletedAt = new Date()
    post.body = null
    post.mediaUrl = null
    post.mediaMeta = null
    emit('channels')
  },

  /**
   * Anonymous, and that is the point: a tally per emoji and no user id, so a
   * reaction cannot be taken back by the same person and cannot be used to
   * enumerate followers. Those two are the same property.
   */
  async react (postId, emoji) {
    await latency(60)
    const { post } = findPost(postId)
    if (!post || post.deletedAt) throw new ZivError(ErrorCode.NOT_FOUND, 'Post not found.')
    post.reactions[emoji] = (post.reactions[emoji] ?? 0) + 1
    emit('channels')
    return { ...post.reactions }
  },

  async markRead (channelId) {
    await latency(40)
    const follow = state.channels.get(channelId)?.followers.get(me())
    if (!follow) return
    follow.lastReadAt = new Date()
    emit('channels')
  },

  async delete (channelId) {
    await latency()
    const record = requireChannel(channelId)
    if (record.admins.get(me()) !== 'owner') {
      throw new ZivError(ErrorCode.PERMISSION, 'Only the owner can delete this channel.')
    }
    state.channels.delete(channelId)
    emit('channels')
  }
}
